import { directive, AttributePart, Part } from "lit-html";
import { URLsField, ValueField } from "Components/main-menu/KenticoKontent";

type UrlSource = string | URLsField | ValueField | ReadonlyArray<string> | null | undefined;

export const resolveUrl = (source: UrlSource): string | undefined => {
	let raw: unknown;
	switch (typeof source) {
		case "string":
			raw = source;
			break;
		case "object":
			if (source === null) {
				raw = undefined;
			} else if (Array.isArray((source as URLsField).urls)) {
				raw = (source as URLsField).urls[0];
			} else if (Array.isArray(source)) {
				raw = (source as ReadonlyArray<string>)[0];
			} else {
				raw = (source as ValueField).value;
			}
			break;
		default:
			raw = undefined;
	}

	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim();
	return trimmed === "" || trimmed === "undefined" || trimmed === "null" ? undefined : trimmed;
};

/**
 * lit-html attribute directive for any attribute whose value triggers a network
 * request (`src`, `srcset`, `href`, ...). When the Kentico value is missing the
 * attribute is removed entirely instead of being emitted as an empty /
 * "undefined" string, so the browser never issues a bogus request.
 *
 * Usage: `` html`<img src=${safeUrl(kontent.img)} />` ``
 */
export const safeUrl = directive((source: UrlSource) => (part: Part) => {
	const url = resolveUrl(source);
	if (part instanceof AttributePart) {
		const { committer } = part;
		if (url === undefined) {
			const { element, name } = committer;
			if (element.hasAttribute(name)) {
				element.removeAttribute(name);
			}
			return;
		}
		part.setValue(url);
		return;
	}
	part.setValue(url ?? "");
});

/**
 * Builds a safe `background-image` declaration for inline `style` attributes.
 * Returns an empty string (no declaration, harmless `style=""`) when the
 * Kentico value is missing, so the browser never fetches `url(undefined)`.
 *
 * Usage: `` html`<div style=${backgroundImage(widget.banner)}></div>` ``
 */
export const backgroundImage = (source: UrlSource): string => {
	const url = resolveUrl(source);
	return url === undefined ? "" : `background-image: url(${url})`;
};
